import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { IMAGE_BASE_URL } from '../services/utils';
import { defaultAvatars } from '../services/avatars';
import ReportModal from '../components/ReportModal';

const defaultAvatar = defaultAvatars[0];
const POLLING_INTERVAL = 5000;

function formatHora(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const hoy = new Date();
  const esHoy = d.toDateString() === hoy.toDateString();
  if (esHoy) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

function MensajesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversaciones, setConversaciones] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [loadingConv, setLoadingConv] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  const bottomRef = useRef(null);
  const scrollContainerRef = useRef(null); // Ref para el contenedor de mensajes
  const pollingRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const itemId = searchParams.get('item');
  const otroId = searchParams.get('usuario');

  const convActiva = conversaciones.find(
    c => String(c.item_id) === String(itemId) && String(c.otro_usuario?.id) === String(otroId)
  );

  useEffect(() => {
    api.get('/users/me').then(r => setCurrentUser(r.data)).catch(() => {});
  }, []);

  const fetchConversaciones = useCallback(async () => {
    try {
      const res = await api.get('/mensajes/conversaciones');
      setConversaciones(res.data);
    } catch (e) {
      console.error('Error cargando conversaciones:', e);
    } finally {
      setLoadingConv(false);
    }
  }, []);

  useEffect(() => { fetchConversaciones(); }, [fetchConversaciones]);

  const fetchMensajes = useCallback(async () => {
    if (!itemId || !otroId) return;
    try {
      const res = await api.get(`/mensajes/${itemId}/${otroId}`);
      setMensajes(res.data);
      fetchConversaciones();
    } catch (e) {
      console.error('Error cargando mensajes:', e);
    }
  }, [itemId, otroId, fetchConversaciones]);

  useEffect(() => {
    if (!itemId || !otroId) return;
    setLoadingMsgs(true);
    fetchMensajes().finally(() => setLoadingMsgs(false));
    pollingRef.current = setInterval(fetchMensajes, POLLING_INTERVAL);
    return () => clearInterval(pollingRef.current);
  }, [itemId, otroId, fetchMensajes]);

  // Lógica de Scroll Inteligente
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Si el usuario está cerca del fondo (margen de 150px), bajamos automáticamente
      const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 150;
      
      if (isAtBottom) {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [mensajes]);

  const handleEnviar = async (e) => {
    e.preventDefault();
    if (!texto.trim() || !itemId || !otroId || enviando) return;
    setEnviando(true);
    try {
      await api.post('/mensajes/', {
        destinatario_id: parseInt(otroId),
        item_id: parseInt(itemId),
        texto: texto.trim()
      });
      setTexto('');
      await fetchMensajes();
      
      // Al enviar nosotros, SIEMPRE forzamos el scroll al fondo
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        inputRef.current?.focus();
      }, 50);
    } catch (err) {
      alert('Error al enviar mensaje.');
    } finally {
      setEnviando(false);
    }
  };

  const handleAdjunto = async (e) => {
    const file = e.target.files[0];
    if (!file || !itemId || !otroId) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo no puede superar los 5MB.');
      return;
    }

    setUploadingFile(true);
    try {
      const presignedRes = await api.get('/upload/presigned-url', {
        params: { content_type: file.type }
      });
      const { presigned_url, filename } = presignedRes.data;

      await fetch(presigned_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file
      });

      const esImagen = file.type.startsWith('image/');
      const textoMensaje = esImagen
        ? `[imagen:${filename}]`
        : `[archivo:${filename}:${file.name}]`;

      await api.post('/mensajes/', {
        destinatario_id: parseInt(otroId),
        item_id: parseInt(itemId),
        texto: textoMensaje
      });

      await fetchMensajes();
      // Scroll al fondo tras subir archivo
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      alert('Error al enviar archivo.');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const renderTextoMensaje = (texto) => {
    if (texto.startsWith('[imagen:') && texto.endsWith(']')) {
      const filename = texto.slice(8, -1);
      return (
        <a href={`${IMAGE_BASE_URL}${filename}`} target="_blank" rel="noreferrer">
          <img
            src={`${IMAGE_BASE_URL}${filename}`}
            alt="adjunto"
            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', display: 'block' }}
          />
        </a>
      );
    }
    if (texto.startsWith('[archivo:') && texto.endsWith(']')) {
      const partes = texto.slice(9, -1).split(':');
      const filename = partes[0];
      const nombreOriginal = partes[1] || 'archivo';
      return (
        <a
          href={`${IMAGE_BASE_URL}${filename}`}
          target="_blank"
          rel="noreferrer"
          className="d-flex align-items-center gap-2 text-decoration-none"
        >
          <i className="bi bi-file-earmark-arrow-down fs-4"></i>
          <span style={{ fontSize: '0.85rem' }}>{nombreOriginal}</span>
        </a>
      );
    }
    return texto;
  };

  const abrirConversacion = (itemId, otroId) => {
    setSearchParams({ item: itemId, usuario: otroId });
  };

  return (
    <div className="row g-0" style={{ position: 'fixed', top: '75px', bottom: '0', left: '0', right: '0', overflow: 'hidden' }}>

      {/* Panel izquierdo: Lista de chats */}
      <div className="col-md-4 border-end d-flex flex-column bg-white" style={{ overflowY: 'auto', height: '100%' }}>
        <div className="p-3 border-bottom bg-white sticky-top">
          <h5 className="mb-0 fw-bold">Mensajes</h5>
        </div>

        {loadingConv ? (
          <div className="text-center p-4 text-muted">Cargando...</div>
        ) : conversaciones.length === 0 ? (
          <div className="text-center p-5 text-muted">
            <i className="bi bi-chat-dots fs-1 d-block mb-3"></i>
            <p className="mb-1">No tenes conversaciones aun.</p>
            <small>Hace clic en "Contactar" en cualquier publicacion para empezar.</small>
          </div>
        ) : (
          conversaciones.map((conv) => {
            const activa = String(conv.item_id) === String(itemId) && String(conv.otro_usuario?.id) === String(otroId);
            return (
              <div
                key={`${conv.item_id}-${conv.otro_usuario?.id}`}
                className={`d-flex align-items-center p-3 border-bottom ${activa ? 'bg-primary bg-opacity-10' : 'bg-white'}`}
                style={{ cursor: 'pointer' }}
                onClick={() => abrirConversacion(conv.item_id, conv.otro_usuario?.id)}
              >
                <div className="position-relative me-3 flex-shrink-0">
                  <img
                    src={conv.otro_usuario?.avatar_url || defaultAvatar}
                    alt={conv.otro_usuario?.username}
                    className="rounded-circle"
                    style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                  />
                  {conv.no_leidos > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.65rem' }}>
                      {conv.no_leidos}
                    </span>
                  )}
                </div>
                <div className="flex-grow-1 overflow-hidden">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className={`fw-semibold ${conv.no_leidos > 0 ? 'text-dark' : 'text-secondary'}`} style={{ fontSize: '0.9rem' }}>
                      {conv.otro_usuario?.username}
                    </span>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {formatHora(conv.fecha_ultimo_mensaje)}
                    </small>
                  </div>
                  <div className="text-muted text-truncate" style={{ fontSize: '0.8rem' }}>
                    <span className="text-primary me-1" style={{ fontSize: '0.75rem' }}>{conv.item_titulo}</span>
                  </div>
                  <div className={`text-truncate ${conv.no_leidos > 0 ? 'fw-semibold text-dark' : 'text-muted'}`} style={{ fontSize: '0.82rem' }}>
                    {conv.ultimo_mensaje.startsWith('[imagen:') ? '📷 Imagen' :
                     conv.ultimo_mensaje.startsWith('[archivo:') ? '📎 Archivo' :
                     conv.ultimo_mensaje}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Panel derecho: Chat activo */}
      <div className="col-md-8 d-flex flex-column bg-light">
        {!itemId || !otroId ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
            <i className="bi bi-chat-text fs-1 mb-3"></i>
            <p>Selecciona una conversacion para empezar</p>
          </div>
        ) : (
          <>
            {/* Header del Chat */}
            <div className="p-3 border-bottom bg-white d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                {convActiva && (
                  <>
                    <img
                      src={convActiva.otro_usuario?.avatar_url || defaultAvatar}
                      alt={convActiva.otro_usuario?.username}
                      className="rounded-circle"
                      style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                    />
                    <div>
                      <div className="fw-semibold">{convActiva.otro_usuario?.username}</div>
                      <Link to={`/items/${convActiva.item_id}`} className="text-muted text-decoration-none" style={{ fontSize: '0.8rem' }}>
                        <i className="bi bi-box-seam me-1"></i>
                        {convActiva.item_titulo}
                      </Link>
                    </div>
                  </>
                )}
              </div>
              {convActiva && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setShowReportModal(true)}
                >
                  <i className="bi bi-flag me-1"></i> Reportar
                </button>
              )}
            </div>

            {/* Area de Mensajes con Scroll Independiente */}
            <div 
              ref={scrollContainerRef}
              className="flex-grow-1 p-3" 
              style={{ overflowY: 'auto', backgroundColor: '#f8f9fa', height: '0' }}
            >
              {loadingMsgs ? (
                <div className="text-center text-muted pt-4">Cargando mensajes...</div>
              ) : mensajes.length === 0 ? (
                <div className="text-center text-muted pt-4">
                  <i className="bi bi-chat-dots fs-2 d-block mb-2"></i>
                  <small>Empieza la conversacion</small>
                </div>
              ) : (
                mensajes.map((msg) => {
                  const esMio = msg.remitente_id === currentUser?.id;
                  return (
                    <div key={msg.id} className={`d-flex mb-3 ${esMio ? 'justify-content-end' : 'justify-content-start'}`}>
                      {!esMio && (
                        <img
                          src={msg.remitente?.avatar_url || defaultAvatar}
                          alt={msg.remitente?.username}
                          className="rounded-circle me-2 align-self-end flex-shrink-0"
                          style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                        />
                      )}
                      <div style={{ maxWidth: '70%' }}>
                        <div
                          className={`p-2 px-3 rounded-3 ${esMio ? 'bg-primary text-white' : 'bg-white border'}`}
                          style={{ wordBreak: 'break-word' }}
                        >
                          {renderTextoMensaje(msg.texto)}
                        </div>
                        <div className={`text-muted mt-1 ${esMio ? 'text-end' : ''}`} style={{ fontSize: '0.72rem' }}>
                          {formatHora(msg.fecha)}
                          {esMio && (
                            <i className={`bi ms-1 ${msg.leido ? 'bi-check2-all text-primary' : 'bi-check2'}`}></i>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input de Mensaje */}
            <div className="p-3 border-top bg-white">
              <form onSubmit={handleEnviar} className="d-flex gap-2 align-items-center">
                <label
                  className={`btn btn-outline-secondary px-2 ${uploadingFile ? 'disabled' : ''}`}
                  style={{ cursor: 'pointer' }}
                >
                  {uploadingFile
                    ? <span className="spinner-border spinner-border-sm" />
                    : <i className="bi bi-paperclip fs-5"></i>
                  }
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept="image/*,.pdf,.doc,.docx"
                    onChange={handleAdjunto}
                    disabled={uploadingFile}
                  />
                </label>

                <input
                  ref={inputRef}
                  type="text"
                  className="form-control"
                  placeholder="Escribi un mensaje..."
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  disabled={enviando}
                  autoFocus
                />
                <button
                  type="submit"
                  className="btn btn-primary px-3"
                  disabled={!texto.trim() || enviando}
                >
                  <i className="bi bi-send-fill"></i>
                </button>
              </form>
            </div>
          </>
        )}
      </div>

      {convActiva && (
        <ReportModal
          show={showReportModal}
          onHide={() => setShowReportModal(false)}
          usuarioId={convActiva.otro_usuario?.id}
          nombre={convActiva.otro_usuario?.username}
        />
      )}
    </div>
  );
}

export default MensajesPage;
